import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { formatCurrency, formatDate } from '@/lib/format';
import { TechnicianProfileFormDialog } from '@/components/technicians/TechnicianProfileFormDialog';
import { EmergencyContactDialog } from '@/components/technicians/EmergencyContactDialog';
import { TechnicianPhotoUpload } from '@/components/technicians/TechnicianPhotoUpload';
import { TechnicianLicenseSection } from '@/components/technicians/TechnicianLicenseSection';
import { TechnicianSkillSection } from '@/components/technicians/TechnicianSkillSection';
import { TechnicianVehicleSection } from '@/components/technicians/TechnicianVehicleSection';
import { TechnicianAvailabilitySection } from '@/components/technicians/TechnicianAvailabilitySection';
import { LicenseExpiryBadge } from '@/components/technicians/LicenseExpiryBadge';
import { toNumericCoordinate } from '@/routes/properties/useProperty';
import {
  TechnicianProfileNotPermittedError,
  useMyTechnicianProfile,
  useTechnicianProfileByUser,
} from './technicians/useTechnicianProfile';

/** Five decimals is about a metre — enough to recognise a pin, not a survey. */
function formatCoordinate(value: number | null | undefined): string {
  return value == null ? '' : value.toFixed(5);
}

/**
 * A technician's profile (M1-24) — slice (a) of the story "TechnicianProfile,
 * licenses, skills, availability" turned out to be. Licenses, skills and
 * availability are separate screens over separate entities (M1-37/38/39),
 * not tabs on this page yet.
 *
 * <b>Reached at `/technicians/:userId`, gated on `jobs.work`</b> — the one
 * permission every role holds (Owner/Admin/Dispatcher hold more; a Technician
 * holds only this) — same shape `PropertyDetail` uses. Which of two DIFFERENT
 * server lookups runs depends on the viewer's PERMISSIONS, not merely on
 * whose profile this is: `GET /technician-profiles/me` (bare authentication)
 * is for a viewer looking at their own profile who could not reach it any
 * other way, because they hold neither `users.read` nor `users.manage` and
 * would 403 on the userId-filtered list. Everyone else — including an
 * owner/admin looking at their own profile, who can edit it and therefore
 * needs the rates `/me` always strips — goes through that list.
 */
export function TechnicianProfileDetail() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const hasPermission = useHasPermission();
  const canManage = hasPermission(Permissions.manageUsers);
  const canReadAny = hasPermission(Permissions.readUsers);
  // `/fleet` is `users.read`-gated (M1-27), so this is the same permission
  // under the name that says what it is FOR: a Dispatcher can look a truck up
  // and cannot edit it, and both are true at once.
  const canViewFleet = canReadAny;

  const currentUser = useCurrentUser();
  const isOwn = currentUser.data?.userId === userId;

  // `/me` is for the caller who has no OTHER way in — a Technician, who holds
  // neither `users.read` nor `users.manage` and would 403 on the byUser
  // lookup. Someone who holds `users.manage` does have another way in, and
  // has to use it even for their own profile: `/me` strips rates for
  // everyone (api/CONVENTIONS.md), and this page hands its profile straight
  // to the edit form, which would then save those blanked-out rates back as
  // zero. A small shop's Owner who also takes calls is exactly that case.
  const useMine = isOwn && !canManage;
  const mine = useMyTechnicianProfile(useMine);
  const others = useTechnicianProfileByUser(userId, currentUser.data !== undefined && !useMine);

  // Only needed to TITLE the "create a profile" dialog before one exists —
  // once a profile exists, its own response already carries the user's name.
  const targetUser = useQuery({
    queryKey: ['users', userId],
    enabled: canManage && userId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/users/{id}', {
        params: { path: { id: userId! } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/users/{id} failed: ${response.status}`);
      }
      return data;
    },
  });

  const [dialog, setDialog] = useState<'create' | 'edit' | 'contact' | null>(null);
  const [announcement, setAnnouncement] = useState('');

  if (currentUser.isPending) {
    return <LoadingState label={t('technicianProfile.loading')} />;
  }

  if (!isOwn && !canManage && !canReadAny) {
    return (
      <EmptyState
        title={t('technicianProfile.notPermittedTitle')}
        description={t('technicianProfile.notPermittedDescription')}
      />
    );
  }

  const query = useMine ? mine : others;

  if (query.error instanceof TechnicianProfileNotPermittedError) {
    return (
      <EmptyState
        title={t('technicianProfile.notPermittedTitle')}
        description={t('technicianProfile.notPermittedDescription')}
      />
    );
  }

  if (query.isPending) {
    return <LoadingState label={t('technicianProfile.loading')} />;
  }

  if (query.isError) {
    return (
      <ErrorState
        title={t('technicianProfile.loadErrorTitle')}
        description={t('technicianProfile.loadErrorDescription')}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const profile = query.data;

  if (!profile) {
    return (
      <section className="space-y-4">
        <EmptyState
          title={t('technicianProfile.noProfileTitle')}
          description={
            canManage
              ? t('technicianProfile.noProfileDescriptionCanManage')
              : t('technicianProfile.noProfileDescriptionReadOnly')
          }
        />
        {canManage && userId ? (
          <div className="flex justify-center">
            <Button type="button" size="sm" onClick={() => setDialog('create')}>
              {t('technicianProfile.createAction')}
            </Button>
          </div>
        ) : null}

        {/* Skills key on the USER, not the profile (M1-38), so this
            renders even here — a small shop's Owner who also estimates has
            skills worth recording and will never have a technician profile.
            That is the payoff of the keying decision, not an accident. */}
        {userId ? (
          <TechnicianSkillSection
            userId={userId}
            canManage={canManage}
            onAnnounce={setAnnouncement}
          />
        ) : null}

        {/* Vehicle assignments key on the USER too (M1-27), following the
            rule M1-39 wrote down: dispatch reads this, and dispatch speaks in
            user ids. Same payoff as the sections either side of it — it
            renders on the no-profile branch, which on the real dev data is
            every technician there is. */}
        {userId ? <TechnicianVehicleSection userId={userId} canViewFleet={canViewFleet} /> : null}

        {/* Availability keys on the USER too (M1-39), for the same reason and
            one more: dev has three technicians and no profiles at all, so a
            profile-keyed section would render for nobody. */}
        {userId ? (
          <TechnicianAvailabilitySection
            userId={userId}
            canManage={canManage}
            onAnnounce={setAnnouncement}
          />
        ) : null}

        {dialog === 'create' && userId ? (
          <TechnicianProfileFormDialog
            userId={userId}
            userName={targetUser.data?.name ?? ''}
            onClose={() => setDialog(null)}
            onSaved={setAnnouncement}
          />
        ) : null}

        <p role="status" aria-live="polite" className="sr-only">
          {announcement}
        </p>
      </section>
    );
  }

  const locationText =
    profile.startLocation === 'Shop'
      ? t('technicianProfile.location.shop')
      : profile.geocodeStatus === 'Geocoded'
        ? t('technicianProfile.location.geocoded', {
            address: profile.homeAddress?.line1,
            latitude: formatCoordinate(toNumericCoordinate(profile.latitude)),
            longitude: formatCoordinate(toNumericCoordinate(profile.longitude)),
          })
        : t('technicianProfile.location.pending', { address: profile.homeAddress?.line1 });

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{profile.userName}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {profile.isOnCall ? (
              <span className="inline-block rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-medium">
                {t('technicianProfile.onCall')}
              </span>
            ) : null}
            {/* M1-37: the AC's "badge on the tech profile", from the counts
                the profile response already carries — no extra request, and
                the same component the roster uses. */}
            <LicenseExpiryBadge summary={profile.licenses} />
          </div>
        </div>
        {canManage ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setDialog('edit')}>
            {t('technicianProfile.editAction')}
          </Button>
        ) : null}
      </div>

      {canManage ? <TechnicianPhotoUpload profile={profile} /> : null}

      {profile.shortBio ? <p className="text-sm">{profile.shortBio}</p> : null}

      <div className="rounded-lg border p-4">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianProfile.hireDate')}
            </dt>
            <dd>
              {profile.hireDate
                ? // hireDate is a bare DateOnly string ("2024-01-15"), which
                  // `new Date(...)` anchors at UTC midnight — formatting it in
                  // the viewer's own zone would print the WRONG calendar date
                  // for anyone west of UTC. UTC here reproduces the literal
                  // date, which is the only one a hire date ever means.
                  formatDate(profile.hireDate, { dateStyle: 'medium', timeZone: 'UTC' })
                : t('technicianProfile.notSet')}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianProfile.employeeRef')}
            </dt>
            <dd>{profile.employeeRef ?? t('technicianProfile.notSet')}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianProfile.startLocation')}
            </dt>
            <dd data-testid="technician-location">{locationText}</dd>
          </div>
          {profile.costRateHourly != null ? (
            <div>
              <dt className="text-xs text-[var(--color-muted-foreground)]">
                {t('technicianProfile.costRateHourly')}
              </dt>
              <dd data-testid="technician-cost-rate">
                {t('technicianProfile.perHour', {
                  amount: formatCurrency(Number(profile.costRateHourly)),
                })}
              </dd>
            </div>
          ) : null}
          {/* M2-33: a null billable rate means two things — hidden (no
              users.manage, and then the cost rate is null too) or not set
              (the price book's labor rate). The cost rate tells them apart. */}
          {profile.costRateHourly != null ? (
            <div>
              <dt className="text-xs text-[var(--color-muted-foreground)]">
                {t('technicianProfile.billableRateHourly')}
              </dt>
              <dd data-testid="technician-billable-rate">
                {profile.billableRateHourly != null
                  ? t('technicianProfile.perHour', {
                      amount: formatCurrency(Number(profile.billableRateHourly)),
                    })
                  : t('technicianProfile.billableRateFromPriceBook')}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('technicianProfile.contact.heading')}</h2>
          {isOwn ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('contact')}>
              {t('technicianProfile.contact.editAction')}
            </Button>
          ) : null}
        </div>
        <dl className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianProfile.contact.name')}
            </dt>
            <dd>{profile.emergencyContactName ?? t('technicianProfile.notSet')}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianProfile.contact.phone')}
            </dt>
            <dd>{profile.emergencyContactPhone ?? t('technicianProfile.notSet')}</dd>
          </div>
        </dl>
      </div>

      {userId ? (
        <TechnicianSkillSection
          userId={userId}
          canManage={canManage}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      {userId ? <TechnicianVehicleSection userId={userId} canViewFleet={canViewFleet} /> : null}

      {userId ? (
        <TechnicianAvailabilitySection
          userId={userId}
          canManage={canManage}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      <TechnicianLicenseSection
        profileId={profile.id}
        technicianName={profile.userName}
        canManage={canManage}
        onAnnounce={setAnnouncement}
      />

      {profile.notes ? (
        <div className="rounded-lg border p-4">
          <h2 className="text-base font-semibold">{t('technicianProfile.notesHeading')}</h2>
          <p className="mt-2 text-sm whitespace-pre-line">{profile.notes}</p>
        </div>
      ) : null}

      <p role="status" aria-live="polite" className="sr-only" data-testid="technician-announcement">
        {announcement}
      </p>

      {dialog === 'edit' && userId ? (
        <TechnicianProfileFormDialog
          userId={userId}
          userName={profile.userName}
          profile={profile}
          onClose={() => setDialog(null)}
          onSaved={setAnnouncement}
        />
      ) : null}

      {dialog === 'contact' ? (
        <EmergencyContactDialog
          profile={profile}
          onClose={() => setDialog(null)}
          onSaved={() => setAnnouncement(t('technicianProfile.contact.saved'))}
        />
      ) : null}
    </section>
  );
}
